import { ICRUDFormDiMetadata } from "@kuika/kuika-cl-model";
import React from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import CreateWorkflowSvg from "../../../svgs/create-hero-svgs/create-workflow.svg";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaCreateHero from "../../ui-kit/kuika-create-hero/kuika-create-hero";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSmallMediumText from "../../ui-kit/typography/SmallMediumText/SmallMediumText";
import { FormWizardCustom } from "../form-wizard/form-wizard-custom";
import { AssignScreenDropdown, IPendingUnmanagedAssignment } from "./step-screen-form-wizard-assign-screen-dropdown";
import "./step-screen-form-wizard-content.scss";

export interface IStepScreenFormWizardContentProps {
  isScreenDoesNotExist: boolean;
  isScreenUnmanaged: boolean;
  formMetadata: ICRUDFormDiMetadata | null;
  onMetadataChange: (metadata: ICRUDFormDiMetadata) => void;
  selectedProcessId: string | null;
  selectedStepId: string | null;
  onClickCreateWorkflowScreen: (processId: string, stepId: string) => void;
  onClickCreateAllWorkflowScreens?: () => void;
  getStepsWithoutScreens?: () => { processId: string; stepId: string; processName: string; stepName: string }[];
  pendingUnmanagedAssignments?: IPendingUnmanagedAssignment[];
  pendingUnmanagedScreenId?: string;
  onChangePendingUnmanagedAssignment?: (processId: string, stepId: string, screenId: string | null) => void;
  progressInfo?: {
    currentStep: number;
    totalSteps: number;
    currentProcessId?: string;
    currentProcessName?: string;
    currentStepId?: string;
    currentStepName?: string;
    completedSteps: any[];
    errors: { processId: string; stepId: string; error: string }[];
  } | null;
  isStepSelectionLoading?: boolean;
}

export interface IStepScreenFormWizardContentState {
  isBulkLoading?: boolean;
}

class StepScreenFormWizardContent extends React.PureComponent<
  IStepScreenFormWizardContentProps & IWithSDProps,
  IStepScreenFormWizardContentState
> {
  constructor(props) {
    super(props);
    this.state = { isBulkLoading: false };
  }

  onClickCreateWorkflowScreen = async () => {
    if (!this.props.selectedProcessId || !this.props.selectedStepId) {
      throw new Error("Process id or step id is not selected");
    }

    this.props.onClickCreateWorkflowScreen(this.props.selectedProcessId, this.props.selectedStepId);
  };

  onChangePendingScreen = (screenId: string | null) => {
    if (!this.props.selectedProcessId || !this.props.selectedStepId || !this.props.onChangePendingUnmanagedAssignment) {
      return;
    }

    this.props.onChangePendingUnmanagedAssignment(this.props.selectedProcessId, this.props.selectedStepId, screenId);
  };

  onClickCreateAllWorkflowScreens = async () => {
    if (!this.props.onClickCreateAllWorkflowScreens) return;
    this.setState({ isBulkLoading: true });
    try {
      await this.props.onClickCreateAllWorkflowScreens();
    } finally {
      this.setState({ isBulkLoading: false });
    }
  };

  render() {
    // TODO: IA: Conditions below are heavily bug-prone.
    const stepsWithoutScreens = this.props.getStepsWithoutScreens?.() || [];
    const showQuickCreate =
      stepsWithoutScreens.length > 0 && !this.props.selectedProcessId && !this.props.selectedStepId;

    // Show loading spinner when step selection is loading or when bulk creation flow is running
    if (this.props.isStepSelectionLoading || this.state.isBulkLoading) {
      return (
        <div className="ssfw-content">
          <div className="ssfw-content__loading">
            <KuikaSpinnerSmall spinning size="large" />
          </div>
        </div>
      );
    }

    return (
      <div className="ssfw-content">
        <div className="ssfw-content__panes">
          {showQuickCreate ? (
            <KuikaCreateHero
              illustration={<CreateWorkflowSvg />}
              title="Get Started"
              description={
                <>
                  Found {stepsWithoutScreens.length} step{stepsWithoutScreens.length !== 1 ? "s" : ""} without screens.
                  Create screens for all steps at once to get started quickly.
                </>
              }
            >
              <KuikaButton onClick={this.onClickCreateAllWorkflowScreens} data-testid="workflowScreensCreateAll">
                CREATE ALL SCREENS
                <span className="ssfw-content__count">{stepsWithoutScreens.length}</span>
              </KuikaButton>
            </KuikaCreateHero>
          ) : !this.props.selectedProcessId || !this.props.selectedStepId ? (
            <KuikaCreateHero
              illustration={<CreateWorkflowSvg />}
              title="Select a step to get started"
              description="To start working on a screen, you need to select a step from the left sidebar."
            />
          ) : null}

          {!this.props.isScreenUnmanaged &&
            this.props.isScreenDoesNotExist &&
            this.props.selectedProcessId &&
            this.props.selectedStepId && (
              <KuikaCreateHero
                illustration={<CreateWorkflowSvg />}
                title="No screen is found for the selected step."
                description="It seems that there is no screen for the selected step. You can create a new screen for this step, or select an existing one."
              >
                <div className="ssfw-empty-state-actions">
                  <KuikaButton onClick={this.onClickCreateWorkflowScreen}>CREATE</KuikaButton>
                  {this.props.onChangePendingUnmanagedAssignment && (
                    <>
                      <span className="ssfw-empty-state-or">or</span>
                      <div className="ssfw-empty-state-dropdown">
                        <AssignScreenDropdown
                          key={`${this.props.selectedProcessId}-${this.props.selectedStepId}`}
                          value={this.props.pendingUnmanagedScreenId}
                          onChange={this.onChangePendingScreen}
                          currentProcessId={this.props.selectedProcessId}
                          currentStepId={this.props.selectedStepId}
                          pendingAssignments={this.props.pendingUnmanagedAssignments}
                        />
                      </div>
                    </>
                  )}
                </div>
                {this.props.pendingUnmanagedScreenId && (
                  <div className="ssfw-empty-state-pending">
                    <KuikaSmallMediumText>
                      This screen will be bound to the step when you click Save.
                    </KuikaSmallMediumText>
                  </div>
                )}
              </KuikaCreateHero>
            )}

          {!this.props.isScreenDoesNotExist &&
            this.props.isScreenUnmanaged &&
            this.props.selectedProcessId &&
            this.props.selectedStepId && (
              <KuikaCreateHero
                illustration={<CreateWorkflowSvg />}
                title="Selected screen is unmanaged"
                description="It seems that the selected screen is unmanaged. If you want to manage this screen with this tool, you should delete the screen and create a new one with this tool."
              />
            )}

          {!this.props.isScreenUnmanaged &&
            !this.props.isScreenDoesNotExist &&
            this.props.selectedProcessId &&
            this.props.selectedStepId &&
            this.props.formMetadata && (
              <FormWizardCustom
                key={`${this.props.selectedProcessId}-${this.props.selectedStepId}`}
                mode="embed"
                formStatus="draft"
                metadata={this.props.formMetadata}
                onMetadataChange={this.props.onMetadataChange}
              />
            )}
        </div>
      </div>
    );
  }
}

const stepScreenFormWizardContent = withSD<IStepScreenFormWizardContentProps>(StepScreenFormWizardContent);
export { stepScreenFormWizardContent as StepScreenFormWizardContent };
